import { isNode, URLBuilder } from "ms-rest-js";
/**
 * Append a string to URL path. Will remove duplicated "/" in front of the string
 * when URL path ends with a "/".
 *
 * @export
 * @param {string} url Source URL string
 * @param {string} name String to be appended to URL
 * @returns {string} An updated URL string
 */

export function appendToURLPath(url, name) {
  var urlParsed = URLBuilder.parse(url);
  var path = urlParsed.getPath();
  path = path ? path.endsWith("/") ? "" + path + name : path + "/" + name : name;
  urlParsed.setPath(path);
  return urlParsed.toString();
}
/**
 * Set URL parameter name and value. If name exists in URL parameters, old value
 * will be replaced by name key. If not provide value, the parameter will be deleted.
 *
 * @export
 * @param {string} url Source URL string
 * @param {string} name Parameter name
 * @param {string} [value] Parameter value
 * @returns {string} An updated URL string
 */

export function setURLParameter(url, name, value) {
  var urlParsed = URLBuilder.parse(url);
  urlParsed.setQueryParameter(name, value);
  return urlParsed.toString();
}
/**
 * Get URL parameter by name.
 *
 * @export
 * @param {string} url
 * @param {string} name
 * @returns {(string | string[] | undefined)}
 */

export function getURLParameter(url, name) {
  var urlParsed = URLBuilder.parse(url);
  return urlParsed.getQueryParameterValue(name);
}
/**
 * Set URL host.
 *
 * @export
 * @param {string} url Source URL string
 * @param {string} host New host string
 * @returns An updated URL string
 */

export function setURLHost(url, host) {
  var urlParsed = URLBuilder.parse(url);
  urlParsed.setHost(host);
  return urlParsed.toString();
}
/**
 * Get URL path from an URL string.
 *
 * @export
 * @param {string} url Source URL string
 * @returns {(string | undefined)}
 */

export function getURLPath(url) {
  var urlParsed = URLBuilder.parse(url);
  return urlParsed.getPath();
}
/**
 * Get URL query key value pairs from an URL string.
 *
 * @export
 * @param {string} url
 * @returns {{[key: string]: string}}
 */

export function getURLQueries(url) {
  var queryString = URLBuilder.parse(url).getQuery();

  if (!queryString) {
    return {};
  }

  queryString = queryString.trim();
  queryString = queryString.startsWith("?") ? queryString.substr(1) : queryString;
  var querySubStrings = queryString.split("&");
  querySubStrings = querySubStrings.filter(function (value) {
    var indexOfEqual = value.indexOf("=");
    var lastIndexOfEqual = value.lastIndexOf("=");
    return indexOfEqual > 0 && indexOfEqual === lastIndexOfEqual && lastIndexOfEqual < value.length - 1;
  });
  var queries = {};

  for (var _i = 0, querySubStrings_1 = querySubStrings; _i < querySubStrings_1.length; _i++) {
    var querySubString = querySubStrings_1[_i];
    var splitResults = querySubString.split("=");
    var key = splitResults[0];
    var value = splitResults[1];
    queries[key] = value;
  }

  return queries;
}
/**
 * Rounds a date off to seconds.
 *
 * @export
 * @param {Date} date Input date
 * @returns {string} Date string in ISO8061 format, with no milliseconds component
 */

export function truncatedISO8061Date(date) {
  var dateString = date.toISOString();
  return dateString.substring(0, dateString.length - 1) + "0000" + "Z";
}
/**
 * Base64 encode.
 *
 * @export
 * @param {string} content
 * @returns {string}
 */

export function base64encode(content) {
  return !isNode ? btoa(content) : Buffer.from(content).toString("base64");
}
/**
 * Base64 decode.
 *
 * @export
 * @param {string} encodedString
 * @returns {string}
 */

export function base64decode(encodedString) {
  return !isNode ? atob(encodedString) : Buffer.from(encodedString, "base64").toString();
}
/**
 * Generate a 64 bytes base64 block ID string.
 *
 * @export
 * @param {number} blockIndex
 * @returns {string}
 */

export function generateBlockID(blockIDPrefix, blockIndex) {
  // To generate a 64 bytes base64 string, source string should be 48
  var maxSourceStringLength = 48; // A blob can have a maximum of 100,000 uncommitted blocks at any given time

  var maxBlockIndexLength = 6;
  var maxAllowedBlockIDPrefixLength = maxSourceStringLength - maxBlockIndexLength;

  if (blockIDPrefix.length > maxAllowedBlockIDPrefixLength) {
    blockIDPrefix = blockIDPrefix.slice(0, maxAllowedBlockIDPrefixLength);
  }

  var res = blockIDPrefix + padStart(blockIndex.toString(), maxSourceStringLength - blockIDPrefix.length, "0");
  return base64encode(res);
}
/**
 * String.prototype.padStart()
 *
 * @export
 * @param {string} currentString
 * @param {number} targetLength
 * @param {string} [padString=" "]
 * @returns {string}
 */

export function padStart(currentString, targetLength, padString) {
  if (padString === void 0) {
    padString = " ";
  }

  if (String.prototype.padStart) {
    return currentString.padStart(targetLength, padString);
  }

  padString = padString || " ";

  if (currentString.length > targetLength) {
    return currentString;
  } else {
    targetLength = targetLength - currentString.length;

    if (targetLength > padString.length) {
      padString += padString.repeat(targetLength / padString.length);
    }

    return padString.slice(0, targetLength) + currentString;
  }
}